/**
 * Trust Centre — the Answers tab (authoring the reverse-replies).
 *
 * Tenant-admin screen behind `trust.manage`. Lets an organisation author the
 * pre-answered questions once, tag each with a tier (who outside may read it),
 * and publish it. Unpublished answers are drafts the external portal never
 * serves — the server enforces that; this screen only presents it.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, CardHeader, Button, Field, Pill, Skeleton, ErrorNotice, type Tone } from '../../components/ui';
import { useTrustPortalT } from '../../lib/trustPortalStrings';
import { TRUST_TIERS, type TrustTier } from '@opencomply/shared';

interface Answer {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  tier: TrustTier;
  sourceRef: string | null;
  sortOrder: number;
  isPublished: boolean;
  updatedAt: string;
}

const TIER_TONE: Record<TrustTier, Tone> = { PUBLIC: 'neutral', NDA: 'warning', NAMED: 'serious' };

interface Draft {
  question: string;
  answer: string;
  category: string;
  tier: TrustTier;
  isPublished: boolean;
}

const EMPTY_DRAFT: Draft = { question: '', answer: '', category: '', tier: 'PUBLIC', isPublished: false };

export function TrustAnswersTab() {
  const { tx, txn, formatDate } = useTrustPortalT();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['trust-answers'],
    queryFn: () => api.get<Answer[]>('/trust-centre/answers'),
  });

  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [message, setMessage] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['trust-answers'] });

  const save = useMutation({
    mutationFn: (vars: { id: string | 'new'; body: Draft }) => {
      const payload = {
        question: vars.body.question.trim(),
        answer: vars.body.answer.trim(),
        category: vars.body.category.trim() || null,
        tier: vars.body.tier,
        isPublished: vars.body.isPublished,
      };
      return vars.id === 'new'
        ? api.post<Answer>('/trust-centre/answers', payload)
        : api.put<Answer>(`/trust-centre/answers/${vars.id}`, payload);
    },
    onSuccess: () => {
      setEditing(null);
      setMessage(tx('trust.answers.saved'));
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/trust-centre/answers/${id}`),
    onSuccess: invalidate,
  });

  const seed = useMutation({
    mutationFn: () => api.post<{ created: number; skipped: number }>('/trust-centre/answers/seed', {}),
    onSuccess: (r) => {
      setMessage(r.created > 0 ? txn('trust.answers.seeded', r.created) : tx('trust.answers.seededNone'));
      invalidate();
    },
  });

  const startNew = () => { setDraft(EMPTY_DRAFT); setEditing('new'); setMessage(null); };
  const startEdit = (a: Answer) => {
    setDraft({ question: a.question, answer: a.answer, category: a.category ?? '', tier: a.tier, isPublished: a.isPublished });
    setEditing(a.id);
    setMessage(null);
  };

  const canSave = draft.question.trim().length > 0 && draft.answer.trim().length > 0;

  const tierLabel = (t: TrustTier) => tx(`trust.tier.${t}` as 'trust.tier.PUBLIC');

  const form = useMemo(
    () => (
      <Card className="border-brand/30">
        <Field label={tx('trust.answers.question')} htmlFor="ta-q">
          <textarea id="ta-q" className="oc-input py-2 min-h-[3rem]" rows={2} maxLength={2000}
            value={draft.question} onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))} />
        </Field>
        <Field label={tx('trust.answers.answer')} htmlFor="ta-a">
          <textarea id="ta-a" className="oc-input py-2 min-h-[6rem]" rows={5} maxLength={8000}
            value={draft.answer} onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={tx('trust.answers.category')} hint={tx('trust.answers.categoryHint')} htmlFor="ta-c">
            <input id="ta-c" className="oc-input" maxLength={120}
              value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} />
          </Field>
          <Field label={tx('trust.answers.tier')} hint={tx(`trust.tier.${draft.tier}.hint` as 'trust.tier.PUBLIC.hint')} htmlFor="ta-t">
            <select id="ta-t" className="oc-input" value={draft.tier}
              onChange={(e) => setDraft((d) => ({ ...d, tier: e.target.value as TrustTier }))}>
              {TRUST_TIERS.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
            </select>
          </Field>
        </div>
        <label className="flex items-center gap-2 mt-1 cursor-pointer">
          <input type="checkbox" className="h-4 w-4 accent-[rgb(var(--brand))]"
            checked={draft.isPublished} onChange={(e) => setDraft((d) => ({ ...d, isPublished: e.target.checked }))} />
          <span className="text-sm text-ink">{tx('trust.answers.publish')}</span>
        </label>
        <div className="mt-4 flex gap-2">
          <Button variant="primary" disabled={!canSave || save.isPending}
            onClick={() => editing && save.mutate({ id: editing, body: draft })}>
            {save.isPending ? tx('trust.answers.saving') : tx('trust.answers.save')}
          </Button>
          <Button onClick={() => setEditing(null)}>{tx('trust.answers.cancel')}</Button>
        </div>
      </Card>
    ),
    [draft, editing, canSave, save.isPending], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="oc-h2">{tx('trust.answers.title')}</h2>
        <p className="oc-muted mt-1 max-w-2xl">{tx('trust.answers.subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={startNew}>{tx('trust.answers.add')}</Button>
        <Button onClick={() => seed.mutate()} disabled={seed.isPending}>{tx('trust.answers.seed')}</Button>
        <span className="text-xs text-ink-muted">{tx('trust.answers.seedHint')}</span>
      </div>
      {message && <p role="status" className="text-sm text-good">{message}</p>}

      {editing === 'new' && form}

      {isLoading && <Skeleton className="h-40" />}
      {isError && <ErrorNotice message={tx('trust.answers.loadFailed')} onRetry={() => void refetch()} />}

      {data && data.length === 0 && editing !== 'new' && (
        <Card><p className="text-center text-ink-secondary py-6">{tx('trust.answers.empty')}</p></Card>
      )}

      {data && data.length > 0 && (
        <ul className="space-y-3">
          {data.map((a) => (
            <li key={a.id}>
              {editing === a.id ? form : (
                <Card>
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                    <p className="font-medium text-ink break-words min-w-0">{a.question}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      <Pill tone={TIER_TONE[a.tier]}>{tierLabel(a.tier)}</Pill>
                      <Pill tone={a.isPublished ? 'good' : 'neutral'}>
                        {a.isPublished ? tx('trust.answers.published') : tx('trust.answers.draft')}
                      </Pill>
                    </div>
                  </div>
                  {a.category && <p className="text-xs text-ink-muted mt-0.5">{a.category}</p>}
                  <p className="mt-2 text-ink-secondary whitespace-pre-line break-words">{a.answer}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <Button size="sm" onClick={() => startEdit(a)}>{tx('trust.answers.edit')}</Button>
                    <Button size="sm" variant="ghost" disabled={remove.isPending}
                      onClick={() => { if (window.confirm(tx('trust.answers.deleteConfirm'))) remove.mutate(a.id); }}>
                      {tx('trust.answers.delete')}
                    </Button>
                    {a.sourceRef && <span className="text-xs text-ink-muted ml-auto">{a.sourceRef}</span>}
                    <span className="text-xs text-ink-muted">{formatDate(a.updatedAt)}</span>
                  </div>
                </Card>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
